import React, { useState, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    LinearProgress,
    Alert
} from '@mui/material';
import { Close as CloseIcon, Settings as SettingsIcon } from '@mui/icons-material';
import { useGetPromotionTriggers } from '../../../../client/hooks/products/trigger.queries';
import { api } from '../../../../client/config';
import { 
    useCreatePromotionTrigger, 
    useUpdatePromotionTrigger, 
    useDeletePromotionTrigger,
    useCreatePromotionTriggerSettings,
    useDeletePromotionTriggerSettings
} from '../../../../client/hooks/products/trigger.mutations';
import TriggerForm from './TriggerForm';
import TriggerList from './TriggerList';
import TriggerSettingsModal from './TriggerSettingsModal';

export default function TriggerManagementModal({ open, onClose }) {
    // States
    const [filters, setFilters] = useState({ active: null, promotion_type: '' });
    const [editingTrigger, setEditingTrigger] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [selectedTriggerSettings, setSelectedTriggerSettings] = useState(null);
    const [showTriggerSettings, setShowTriggerSettings] = useState(false);
    const [formData, setFormData] = useState({
        promotion_type: 'SMART',
        expire_in: '',
        trigger_rules: [{ condition: 'PRICE', operator: 'GREATER_THAN', value: 0, metadata: {} }],
        active: true,
        apply_to_all_candidates: false
    });
    const [triggerSettings, setTriggerSettings] = useState([]);
    const [importedSpreadsheetData, setImportedSpreadsheetData] = useState(null);
    const [processingBatches, setProcessingBatches] = useState({ isProcessing: false, current: 0, total: 0 });
    const [triggerSettingsCounts, setTriggerSettingsCounts] = useState({});

    // Hooks
    const { data: triggersData, isLoading, refetch } = useGetPromotionTriggers({
        limit: 50, offset: 0, ...filters
    }, open);
    
    // REMOVIDO: Hook desnecessário que carregava settings logo ao abrir modal
    // Agora cada funcionalidade carrega seus próprios settings quando precisar
    
    const createTriggerMutation = useCreatePromotionTrigger();
    const updateTriggerMutation = useUpdatePromotionTrigger();
    const deleteTriggerMutation = useDeletePromotionTrigger();
    const createSettingsMutation = useCreatePromotionTriggerSettings();
    const deleteSettingsMutation = useDeletePromotionTriggerSettings();

    // Data
    const triggers = triggersData?.results || [];
    const totalTriggers = triggersData?.paging?.total || 0;
    
    // Função para buscar count de settings por trigger
    const fetchTriggerSettingsCount = async (triggerId) => {
        try {
            const response = await api.get('/promotions-trigger/settings', {
                params: { trigger_id: triggerId, limit: 1, offset: 0 }
            });
            return response.data.paging?.total || 0;
        } catch (error) {
            console.error('Erro ao buscar count de settings:', error);
            return 0;
        }
    };
    
    // Effect para buscar counts quando triggers mudarem
    React.useEffect(() => {
        if (triggers.length > 0) {
            const fetchCounts = async () => {
                const counts = {};
                for (const trigger of triggers) {
                    counts[trigger._id] = await fetchTriggerSettingsCount(trigger._id);
                }
                setTriggerSettingsCounts(counts);
            };
            fetchCounts();
        }
    }, [triggers]);
    
    // Limpar dados quando o modal fechar
    React.useEffect(() => {
        if (!open) {
            resetForm();
            setShowForm(false);
            setEditingTrigger(null);
            setSelectedTriggerSettings(null);
        }
    }, [open]);

    // Handlers
    const resetForm = () => {
        setFormData({
            promotion_type: 'SMART',
            expire_in: '',
            trigger_rules: [{ condition: 'PRICE', operator: 'GREATER_THAN', value: 0, metadata: {} }],
            active: true,
            apply_to_all_candidates: false
        });
        setTriggerSettings([]);
        setImportedSpreadsheetData(null);
        setTriggerSettingsCounts({}); // Limpar também os counts
    };

    const handleCreateTrigger = () => {
        setEditingTrigger(null);
        resetForm();
        setShowForm(true);
    };

    const handleEditTrigger = (trigger) => {
        setEditingTrigger(trigger);
        setFormData({
            promotion_type: trigger.promotion_type,
            expire_in: trigger.expire_in || '',
            trigger_rules: trigger.trigger_rules || [{ condition: 'PRICE', operator: 'GREATER_THAN', value: 0, metadata: {} }],
            active: trigger.active,
            apply_to_all_candidates: trigger.apply_to_all_candidates || false
        });
        
        // Settings serão carregados automaticamente pelo TriggerForm com paginação
        setTriggerSettings([]); // Apenas settings novos que o usuário adicionar manualmente
        
        setShowForm(true);
    };

    const handleDeleteTrigger = async (triggerId) => {
        if (window.confirm('Tem certeza que deseja deletar este gatilho?')) {
            try {
                await deleteTriggerMutation.mutateAsync({ triggerId });
                refetch();
            } catch (error) {
                console.error('Erro ao deletar trigger:', error);
            }
        }
    };

    // Função para dividir array em chunks
    const chunkArray = (array, chunkSize) => {
        const chunks = [];
        for (let i = 0; i < array.length; i += chunkSize) {
            chunks.push(array.slice(i, i + chunkSize));
        }
        return chunks;
    };

    const handleSubmitTrigger = async () => {
        try {
            let createdTrigger;
            if (editingTrigger) {
                // Atualiza o trigger
                await updateTriggerMutation.mutateAsync({
                    triggerId: editingTrigger._id,
                    data: formData
                });

                // Gerenciar settings para trigger existente
                const allSettings = [...triggerSettings];
                if (importedSpreadsheetData?.settings) {
                    allSettings.push(...importedSpreadsheetData.settings);
                }
                
                if (allSettings.length > 0) {
                    // Deletar todos os settings existentes deste trigger antes de criar novos
                    // Fazemos uma requisição para buscar os settings existentes apenas quando necessário
                    try {
                        const existingSettingsResponse = await api.get('/promotions-trigger/settings', {
                            params: { trigger_id: editingTrigger._id, limit: 1000, offset: 0 }
                        });
                        
                        const existingSettings = existingSettingsResponse.data.results || [];
                        
                        // Deletar settings existentes
                        for (const setting of existingSettings) {
                            await deleteSettingsMutation.mutateAsync({ 
                                settingsId: setting._id 
                            });
                        }
                    } catch (error) {
                        console.error('Erro ao buscar settings existentes:', error);
                    }

                    // Criar novos settings em lotes para evitar erro 413
                    const settingsWithTriggerId = allSettings.map(setting => ({
                        ...setting,
                        trigger_id: editingTrigger._id
                    }));
                    
                    // Dividir em chunks de 50 (tamanho seguro)
                    const settingsChunks = chunkArray(settingsWithTriggerId, 50);
                    
                    if (settingsChunks.length > 1) {
                        setProcessingBatches({ isProcessing: true, current: 0, total: settingsChunks.length });
                    }
                    
                    // Processar cada chunk sequencialmente
                    for (let i = 0; i < settingsChunks.length; i++) {
                        if (settingsChunks.length > 1) {
                            setProcessingBatches({ isProcessing: true, current: i + 1, total: settingsChunks.length });
                        }
                        
                        await createSettingsMutation.mutateAsync({
                            data: { settings: settingsChunks[i] }
                        });
                    }
                    
                    if (settingsChunks.length > 1) {
                        setProcessingBatches({ isProcessing: false, current: 0, total: 0 });
                    }
                } else {
                    // Se não há settings novos, deletar todos os existentes (se houver)
                    try {
                        const existingSettingsResponse = await api.get('/promotions-trigger/settings', {
                            params: { trigger_id: editingTrigger._id, limit: 1000, offset: 0 }
                        });
                        
                        const existingSettings = existingSettingsResponse.data.results || [];
                        
                        for (const setting of existingSettings) {
                            await deleteSettingsMutation.mutateAsync({ 
                                settingsId: setting._id 
                            });
                        }
                    } catch (error) {
                        console.error('Erro ao buscar settings existentes para deletar:', error);
                    }
                }
            } else {
                // Criar novo trigger
                createdTrigger = await createTriggerMutation.mutateAsync({ data: formData });
                
                const allSettings = [...triggerSettings];
                if (importedSpreadsheetData?.settings) {
                    allSettings.push(...importedSpreadsheetData.settings);
                }
                
                if (allSettings.length > 0 && createdTrigger?._id) {
                    const settingsWithTriggerId = allSettings.map(setting => ({
                        ...setting,
                        trigger_id: createdTrigger._id
                    }));
                    
                    // Dividir em chunks de 50 para evitar erro 413
                    const settingsChunks = chunkArray(settingsWithTriggerId, 50);
                    
                    if (settingsChunks.length > 1) {
                        setProcessingBatches({ isProcessing: true, current: 0, total: settingsChunks.length });
                    }
                    
                    // Processar cada chunk sequencialmente
                    for (let i = 0; i < settingsChunks.length; i++) {
                        if (settingsChunks.length > 1) {
                            setProcessingBatches({ isProcessing: true, current: i + 1, total: settingsChunks.length });
                        }
                        
                        await createSettingsMutation.mutateAsync({
                            data: { settings: settingsChunks[i] }
                        });
                    }
                    
                    if (settingsChunks.length > 1) {
                        setProcessingBatches({ isProcessing: false, current: 0, total: 0 });
                    }
                }
            }
            setShowForm(false);
            // Remover refetch aqui - as invalidateQueries nos hooks já fazem o trabalho
        } catch (error) {
            console.error('Erro ao salvar trigger:', error);
        }
    };

    const handleRuleChange = (index, field, value) => {
        const newRules = [...formData.trigger_rules];
        newRules[index] = { ...newRules[index], [field]: value };
        setFormData({ ...formData, trigger_rules: newRules });
    };

    const addRule = () => {
        setFormData({
            ...formData,
            trigger_rules: [...formData.trigger_rules, {
                condition: 'PRICE', operator: 'GREATER_THAN', value: 0, metadata: {}
            }]
        });
    };

    const removeRule = (index) => {
        const newRules = formData.trigger_rules.filter((_, i) => i !== index);
        setFormData({ ...formData, trigger_rules: newRules });
    };

    const handleAddSetting = () => {
        setTriggerSettings([...triggerSettings, { mlb_item: '', min_price: 0 }]);
    };

    const handleRemoveSetting = (index) => {
        setTriggerSettings(triggerSettings.filter((_, i) => i !== index));
    };

    const handleSettingChange = useCallback((index, field, value) => {
        const newSettings = [...triggerSettings];
        newSettings[index] = { ...newSettings[index], [field]: value };
        setTriggerSettings(newSettings);
    }, [triggerSettings]);
    
    const handleCancelForm = useCallback(() => {
        setShowForm(false);
    }, []);


    const handleViewTriggerSettings = (trigger) => {
        // Agora apenas seta o trigger - o TriggerSettingsModal vai carregar os settings com paginação
        setSelectedTriggerSettings({
            trigger,
            settings: [] // Será carregado pelo modal
        });
        setShowTriggerSettings(true);
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="xl"
                fullWidth
                PaperProps={{
                    sx: { 
                        minHeight: '85vh',
                        borderRadius: 3,
                        boxShadow: '0 12px 40px rgba(255, 152, 0, 0.15)'
                    }
                }}
            >
                <DialogTitle sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    borderBottom: '1px solid #e0e0e0',
                    pb: 3,
                    pt: 3,
                    background: 'linear-gradient(135deg, #fff8f0 0%, #ffffff 100%)'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{
                            width: 6,
                            height: 40,
                            bgcolor: '#ff9800',
                            borderRadius: 1
                        }} />
                        <Box>
                            <Typography variant="h4" sx={{ 
                                color: '#ff9800',
                                fontWeight: 700,
                                mb: 0.5
                            }}>
                                Gerenciamento de Gatilhos
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                Configure triggers automáticos para campanhas promocionais
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton 
                        onClick={onClose} 
                        sx={{ 
                            color: '#666',
                            '&:hover': {
                                bgcolor: 'rgba(255, 152, 0, 0.1)',
                                color: '#ff9800',
                                transform: 'scale(1.1)'
                            },
                            transition: 'all 0.2s ease'
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: 0, bgcolor: '#fafafa' }}>
                    {showForm ? (
                        <Box sx={{ p: 4 }}>
                            <TriggerForm 
                                formData={formData}
                                editingTrigger={editingTrigger}
                                triggerSettings={triggerSettings}
                                onFormDataChange={setFormData}
                                onTriggerSettingsChange={setTriggerSettings}
                                onSubmit={handleSubmitTrigger}
                                onCancel={handleCancelForm}
                                onRuleChange={handleRuleChange}
                                onAddRule={addRule}
                                onRemoveRule={removeRule}
                                onAddSetting={handleAddSetting}
                                onRemoveSetting={handleRemoveSetting}
                                onSettingChange={handleSettingChange}
                                onImportedDataChange={setImportedSpreadsheetData}
                                processingBatches={processingBatches}
                            />
                        </Box>
                    ) : (
                        <Box sx={{ p: 4 }}>
                            <TriggerList 
                                triggers={triggers}
                                totalTriggers={totalTriggers}
                                triggerSettingsCounts={triggerSettingsCounts}
                                filters={filters}
                                onFiltersChange={setFilters}
                                onCreateTrigger={handleCreateTrigger}
                                onEditTrigger={handleEditTrigger}
                                onDeleteTrigger={handleDeleteTrigger}
                                onViewSettings={handleViewTriggerSettings}
                                isLoading={isLoading}
                            />
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ 
                    p: 3, 
                    borderTop: '1px solid #e0e0e0',
                    bgcolor: '#fff8f0'
                }}>
                    <Button 
                        onClick={onClose}
                        variant="outlined"
                        sx={{ 
                            textTransform: 'none',
                            borderColor: '#ff9800',
                            color: '#ff9800',
                            fontWeight: 600,
                            px: 4,
                            py: 1.5,
                            borderRadius: 2,
                            '&:hover': {
                                borderColor: '#f57c00',
                                bgcolor: 'rgba(255, 152, 0, 0.1)'
                            }
                        }}
                    >
                        Fechar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* SÓ RENDERIZAR quando realmente aberto para evitar hooks duplos */}
            {showTriggerSettings && (
                <TriggerSettingsModal 
                    open={showTriggerSettings}
                    onClose={() => setShowTriggerSettings(false)}
                    selectedTriggerSettings={selectedTriggerSettings}
                />
            )}
        </>
    );
}